"use client";
import {
  useState,
  useCallback,
  createContext,
  useContext,
  useMemo,
  useEffect,
  type ReactNode,
} from "react";
import { useRef, type RefObject } from "react";
import { type UseFormReturn, type FieldValues } from "react-hook-form";
interface PromptContextType {
  isOpen: boolean;
  isDirty: boolean;
  confirmedNavigation: boolean;
  handleSetIsOpen: () => boolean;
  handleUnSetIsOpen: () => boolean;
  handleSetIsDirty: () => boolean;
  handleUnSetIsDirty: () => boolean;
  handleSetForm: (form: unknown) => void;
  handleCloseDialog: () => void;
  form: UseFormReturn<FieldValues>;
  formRef: RefObject<HTMLFormElement>;
  isDialogOpen: boolean;
  handleSetIsDialogOpen: (isDialogOpen: boolean) => void;
  handleExtendedDialogOpen: (isDialogOpen: boolean) => void;
  isExtendedDialogOpen: boolean;
}
export const promptOpenContext = createContext<PromptContextType | undefined>(
  undefined,
);

export function UnsavedWarningPromptContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [confirmedNavigation] = useState(false);
  const [saveWork] = useState(false);
  const [form, setForm] = useState<unknown>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isExtendedDialogOpen, setIsExtendedDialogOpen] = useState(false);
  const handleSetIsOpen = useCallback(() => {
    setIsOpen(true);
    return true;
  }, [isOpen, setIsOpen]);

  const handleUnSetIsOpen = useCallback(() => {
    setIsOpen(false);
    return true;
  }, [isOpen, setIsOpen]);

  const handleSetIsDirty = useCallback(() => {
    setIsDirty(true);
    return true;
  }, [setIsDirty, isDirty]);

  const handleUnSetIsDirty = useCallback(() => {
    setIsDirty(false);
    return true;
  }, [setIsDirty, isDirty]);
  const handleSetForm = useCallback((form: unknown) => {
    setForm(form);
    return true;
  }, []);
  const handleCloseDialog = useCallback(() => {
    if (isDirty && !isOpen) {
      setIsOpen(true);
    } else {
      setIsDialogOpen(false);
      setIsExtendedDialogOpen(false);
    }
  }, [isDirty, isOpen]);

  const handleSetIsDialogOpen = useCallback(
    (isOpenDialog: boolean) => {
      setIsDialogOpen(isOpenDialog);
    },
    [setIsDialogOpen],
  );
  const handleExtendedDialogOpen = useCallback(
    (isOpenExtendedDialog: boolean) => {
      setIsExtendedDialogOpen(isOpenExtendedDialog);
    },
    [setIsExtendedDialogOpen],
  );
  useEffect(() => {
    if (!isDirty && !isOpen) {
      setIsDialogOpen(false);
      //setIsEditOpen(false);
    }
  }, [isDirty, isOpen, setIsDialogOpen]);

  const value = useMemo(
    () => ({
      isOpen,
      isDirty,
      confirmedNavigation,
      saveWork,
      handleSetIsOpen,
      handleUnSetIsOpen,
      handleSetIsDirty,
      handleUnSetIsDirty,
      form,
      handleSetForm,
      handleCloseDialog,
      isDialogOpen,
      handleSetIsDialogOpen,
      isExtendedDialogOpen,
      handleExtendedDialogOpen,
    }),
    [
      isOpen,
      isDirty,
      confirmedNavigation,
      saveWork,
      handleSetIsOpen,
      handleUnSetIsOpen,
      handleSetIsDirty,
      handleUnSetIsDirty,
      form,
      handleSetForm,
      handleCloseDialog,
      isDialogOpen,
      handleSetIsDialogOpen,
      isExtendedDialogOpen,
      handleExtendedDialogOpen,
    ],
  );
  return (
    <promptOpenContext.Provider
      value={{ ...value, formRef, form } as PromptContextType}
    >
      {children}
    </promptOpenContext.Provider>
  );
}
export function useSaveWarningPrompt() {
  const context = useContext(promptOpenContext);
  if (context === undefined) {
    throw new Error(
      "save Warning prompt must be used within a PromptContextProvider",
    );
  }
  return context;
}
